import { Loader2, Trash2, X } from "lucide-react";
import { useState } from "react";
import { inputClass, labelClass } from "@/components/admin/companyProfile/styles";
import { useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib/api";
import { brandService } from "@/lib/services";
import type { Brand, BrandCreate, BrandUpdate } from "@/types";
import { BrandKitFields } from "./BrandKitFields";
import {
  type BrandFormState,
  brandToForm,
  emptyBrandForm,
  extractionToForm,
  formTags,
  formToBrandKit,
} from "./brandForm";
import { ExtractPanel } from "./ExtractPanel";
import { LogoSection } from "./LogoSection";

interface BrandFormModalProps {
  /** The brand being edited, or null for create mode. */
  brand: Brand | null;
  onClose: () => void;
  /** Called after a successful create/update so the list can refresh. */
  onSaved: () => void | Promise<void>;
  /** When provided, renders a Delete action (managers of non-default brands). */
  onDelete?: () => void | Promise<void>;
}

/** Create/edit modal for a brand — identity + branding kit + logo. */
export function BrandFormModal({ brand, onClose, onSaved, onDelete }: Readonly<BrandFormModalProps>) {
  const { showToast } = useToast();
  const isEdit = brand !== null;
  const readOnly = isEdit && !brand.canEdit;
  const [form, setForm] = useState<BrandFormState>(() => (brand ? brandToForm(brand) : emptyBrandForm()));
  const [saving, setSaving] = useState(false);
  // Bump to force LogoSection to refetch its preview (the logo URL is path-based
  // and doesn't change when the image is replaced).
  const [logoVersion, setLogoVersion] = useState(0);

  const setField = <K extends keyof BrandFormState>(key: K, value: BrandFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      showToast("Brand name is required", "warning");
      return;
    }
    setSaving(true);
    try {
      const kit = formToBrandKit(form);
      const tags = formTags(form);
      if (isEdit) {
        const payload: BrandUpdate = {
          name: form.name.trim(),
          description: form.description || null,
          brandKit: kit,
          brandStyle: form.brand_style || null,
          audience: form.audience || null,
          tags,
        };
        await brandService.update(brand.uuid, payload);
      } else {
        const payload: BrandCreate = {
          name: form.name.trim(),
          description: form.description || null,
          brandKit: kit,
          brandStyle: form.brand_style || null,
          audience: form.audience || null,
          tags,
        };
        await brandService.create(payload);
      }
      showToast(isEdit ? "Brand updated" : "Brand created", "success");
      await onSaved();
      onClose();
    } catch (err) {
      showToast(getApiErrorMessage(err, isEdit ? "Failed to update brand" : "Failed to create brand"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Use an extracted logo candidate as the brand logo (edit mode only — the brand
  // must already exist to accept a logo upload).
  const handleUseLogoCandidate = async (dataUri: string) => {
    if (!brand) return;
    try {
      const blob = await (await fetch(dataUri)).blob();
      const file = new File([blob], "logo.png", { type: "image/png" });
      await brandService.uploadLogo(brand.uuid, file);
      setLogoVersion((v) => v + 1); // force LogoSection preview refetch
      showToast("Logo set from document", "success");
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to set logo"), "error");
    }
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-content-primary">
            {isEdit ? `Edit “${brand.name}”` : "Create brand"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="p-1.5 hover:bg-interactive-active rounded-lg transition"
          >
            <X className="h-5 w-5 text-content-tertiary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!readOnly && (
            <ExtractPanel
              brand={brand}
              onExtracted={(draft) => setForm((prev) => extractionToForm(draft, prev))}
              onUseLogoCandidate={handleUseLogoCandidate}
            />
          )}

          <div>
            <label htmlFor="brand-name" className={labelClass}>
              Name
            </label>
            <input
              id="brand-name"
              type="text"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="e.g. Acme Internal"
              maxLength={255}
              disabled={readOnly}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="brand-description" className={labelClass}>
              Description
            </label>
            <input
              id="brand-description"
              type="text"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="Short summary shown on the brand card"
              maxLength={2000}
              disabled={readOnly}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="brand-audience" className={labelClass}>
                Audience
              </label>
              <input
                id="brand-audience"
                type="text"
                value={form.audience}
                onChange={(e) => setField("audience", e.target.value)}
                placeholder="e.g. internal, external"
                maxLength={100}
                disabled={readOnly}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="brand-tags" className={labelClass}>
                Tags (comma-separated)
              </label>
              <input
                id="brand-tags"
                type="text"
                value={form.tags}
                onChange={(e) => setField("tags", e.target.value)}
                placeholder="product-x, marketing"
                disabled={readOnly}
                className={inputClass}
              />
            </div>
          </div>

          {/* Logo — only editable once the brand exists (needs a uuid to upload). */}
          {isEdit ? (
            <LogoSection brand={brand} readOnly={readOnly} refreshKey={logoVersion} />
          ) : (
            <p className="text-xs text-content-tertiary">You can add a logo after creating the brand.</p>
          )}

          <BrandKitFields form={form} setField={setField} readOnly={readOnly} />
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-border-primary">
          <div>
            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded-lg transition"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg text-sm font-medium border border-border-secondary transition"
            >
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gradient hover:opacity-90 text-white rounded-lg text-sm font-medium transition disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEdit ? "Save changes" : "Create brand"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
